import React, { ReactNode, useEffect, useState } from 'react';
import wretch from 'wretch';
import { useBuildVars } from '../build-config';
import { SiteConfig, siteConfigContext } from './site-config-context';
import { getSameOriginApiBasePath } from '../../query/utils';
import { disableSameOriginRequests } from '../../query/query-consts';

const apiWretcher = wretch(undefined).options({
  // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
  // credentials: 'include',
  credentials: 'same-origin', // Default

  headers: {
    accept: 'application/json',
    'content-type': 'application/json',
  },

  // https://developer.mozilla.org/en-US/docs/Web/API/Request/mode
  mode: 'cors',
});

type Props = {
  children: ReactNode;
};

export function SiteConfigProvider(props: Props) {
  const { children } = props;

  const buildVars = useBuildVars();

  // TODO: Same-origin Config API cannot be enabled per country as config call contains siteCode.
  //   This logic should be removed once once same-origin APIs is rollouted to ALL countries.
  const shouldUseSameOriginApis =
    buildVars.buildEnvironment !== 'production' &&
    buildVars.buildEnvironment !== 'productionbeta' &&
    buildVars.buildEnvironment !== 'development' &&
    localStorage.getItem(disableSameOriginRequests) !== 'true';

  const [value, setValue] = useState<SiteConfig | undefined>(undefined);

  useEffect(() => {
    let active = true;

    /**
     * Config API response is being cached. The 'ver' query param would be used to invalidate cache if necessary
     */
    const queryParams: { [key: string]: string | undefined } = {
      ver: buildVars['CONFIG_API_VERSION'],
    };

    (async () => {
      let wretcher = apiWretcher.url(
        `${
          shouldUseSameOriginApis
            ? getSameOriginApiBasePath()
            : buildVars.API_BASE
        }/imonline-k8s-cep/cwc/config/v1/config`
      );
      // TODO: When same-origin APIs is rolled out to all countries on prod, the above wretcher definition
      //   can be replaced with the below code to enable it for all envs, all countries.
      // let wretcher = apiWretcher.url(
      //   `${getSameOriginApiBasePath()/imonline-k8s-cep/cwc/config/v1/config`
      // );

      if (globalThis.location.origin.indexOf('://local') > -1) {
        /**
         * Note: hostname query param is always needed when running locally.
         * Even when running stage and production builds locally in express or ngnix.
         */
        queryParams['hostname'] =
          buildVars.appId === 'x4ec'
            ? buildVars.X4EC_DEV_PROXY_HOSTNAME
            : buildVars.DEV_PROXY_HOSTNAME;
      }

      wretcher = wretcher.query(queryParams);

      const siteConfig = await wretcher.get().json<SiteConfig>();

      if (active) {
        setValue(siteConfig);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildVars, setValue]);

  if (!value) {
    return null;
  }

  const { Provider } = siteConfigContext;
  return <Provider value={value}>{children}</Provider>;
}
